/**
 * Email-Link Sign-In Callback Page
 * =============================================================================
 * Handles the redirect from a Firebase email-link sign-in.
 *
 * Flow:
 * 1. User clicks the link in their email → lands here.
 * 2. We read the email from localStorage (saved when the link was requested).
 *    If missing (cross-device, e.g. requested on laptop, clicked on phone),
 *    we prompt the user to re-enter it.
 * 3. signInWithEmailLink() completes auth, AuthContext picks up the new user
 *    via onAuthStateChanged, and we redirect to "/".
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from '../config/firebase';
import { isSignInWithEmailLink, signInWithEmailLink } from 'firebase/auth';
import { isEmailAllowed, getUnauthorizedMessage } from '../config/allowlist';

const STORAGE_KEY = 'emailForSignIn';

const FinishSignInPage: React.FC = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState<'verifying' | 'need-email' | 'error'>('verifying');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [promptedEmail, setPromptedEmail] = useState('');

  const completeSignIn = async (emailToUse: string) => {
    try {
      const result = await signInWithEmailLink(auth, emailToUse, window.location.href);
      const userEmail = result.user.email;

      if (!isEmailAllowed(userEmail)) {
        setErrorMsg(getUnauthorizedMessage(userEmail || undefined));
        await auth.signOut();
        setStatus('error');
        return;
      }

      window.localStorage.removeItem(STORAGE_KEY);
      navigate('/', { replace: true });
    } catch (error: any) {
      console.error('Email-link sign-in failed:', error.code, error.message);
      let msg = error.message || 'Sign-in failed. Please try again.';
      if (error.code === 'auth/invalid-action-code' || error.code === 'auth/expired-action-code') {
        msg = 'This sign-in link has expired or was already used. Please request a new one from the sign-in page.';
      } else if (error.code === 'auth/invalid-email') {
        msg = 'That email doesn\'t match the link. Please request a new sign-in link.';
      }
      setErrorMsg(msg);
      setStatus('error');
    }
  };

  useEffect(() => {
    if (!isSignInWithEmailLink(auth, window.location.href)) {
      navigate('/login', { replace: true });
      return;
    }

    const savedEmail = window.localStorage.getItem(STORAGE_KEY);
    if (savedEmail) {
      completeSignIn(savedEmail);
    } else {
      setStatus('need-email');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onPromptedEmailSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!promptedEmail) return;
    setStatus('verifying');
    completeSignIn(promptedEmail);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-white rounded-xl shadow-xl shadow-slate-200/50 p-8 text-center">
        <img src="/logo.svg" alt="Precepi" className="h-12 mx-auto mb-4" />

        {status === 'verifying' && (
          <>
            <div className="inline-block animate-spin rounded-full h-10 w-10 border-4 border-indigo-600 border-t-transparent mb-4" />
            <p className="text-slate-600">Signing you in…</p>
          </>
        )}

        {status === 'need-email' && (
          <>
            <h2 className="text-xl font-semibold text-slate-800 mb-2">Confirm your email</h2>
            <p className="text-sm text-slate-500 mb-6">
              Looks like you opened this link on a different device. Enter the email address you used to request the link.
            </p>
            <form onSubmit={onPromptedEmailSubmit} className="space-y-4 text-left">
              <input
                type="email"
                value={promptedEmail}
                onChange={(e) => setPromptedEmail(e.target.value)}
                className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                placeholder="you@example.com"
                required
                autoFocus
              />
              <button
                type="submit"
                className="w-full bg-indigo-600 text-white px-4 py-3 rounded-lg hover:bg-indigo-700 transition-colors font-medium"
              >
                Continue
              </button>
            </form>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="text-red-500 text-3xl mb-3">⚠</div>
            <h2 className="text-xl font-semibold text-slate-800 mb-2">Sign-in failed</h2>
            <p className="text-sm text-slate-600 mb-6">{errorMsg}</p>
            <button
              onClick={() => navigate('/login', { replace: true })}
              className="w-full bg-indigo-600 text-white px-4 py-3 rounded-lg hover:bg-indigo-700 transition-colors font-medium"
            >
              Back to sign-in
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default FinishSignInPage;
